/**
 * Bulk Upload Modal for Publishers
 * Multi-step process: File Upload -> Column Mapping -> Preview -> Save
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  Box,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

import PublisherFileUploadStep from './PublisherFileUploadStep';
import PublisherColumnMappingStep from './PublisherColumnMappingStep';
import PublisherPreviewTableStep from './PublisherPreviewTableStep';

export interface ColumnMapping {
  csvColumn: string;
  dbField: string;
}

export interface PreviewRow {
  rowIndex: number;
  data: Record<string, any>;
  isValid: boolean;
  errors: string[];
}

interface PublisherBulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message?: string) => void;
}

const steps = ['Upload File', 'Map Columns', 'Preview & Validate', 'Save'];

const PublisherBulkUploadModal: React.FC<PublisherBulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [parseData, setParseData] = useState<any>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const handleReset = useCallback(() => {
    setActiveStep(0);
    setFile(null);
    setParseData(null);
    setColumnMappings([]);
    setPreviewData([]);
    setSelectedRows([]);
    setError('');
  }, []);

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleFileUpload = async (uploadedFile: File, parsedData: any) => {
    setFile(uploadedFile);
    setParseData(parsedData);
    setColumnMappings(parsedData.autoMappings || []);
    setActiveStep(1);
  };

  const handleMappingComplete = async (mappings: ColumnMapping[]) => {
    setColumnMappings(mappings);
    setLoading(true);

    try {
      // Transform data based on mappings
      const transformedData: PreviewRow[] = parseData!.rows.map((row: any, index: number) => {
        const transformedRow: Record<string, any> = {};
        const errors: string[] = [];

        mappings.forEach(mapping => {
          if (mapping.dbField && mapping.csvColumn) {
            transformedRow[mapping.dbField] = row[mapping.csvColumn];
          }
        });

        // Validate required fields
        if (!transformedRow.publisherName || !transformedRow.publisherName.trim()) {
          errors.push('Publisher Name is required');
        }

        if (!transformedRow.email || !transformedRow.email.trim()) {
          errors.push('Email is required');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(transformedRow.email)) {
          errors.push('Invalid email format');
        }

        return {
          rowIndex: index,
          data: transformedRow,
          isValid: errors.length === 0,
          errors,
        };
      });

      // Check for duplicate emails within the CSV file
      const emailCounts = new Map<string, number[]>();
      transformedData.forEach((row, index) => {
        const email = row.data.email?.trim().toLowerCase();
        if (email) {
          if (!emailCounts.has(email)) {
            emailCounts.set(email, []);
          }
          emailCounts.get(email)!.push(index);
        }
      });

      // Mark duplicate emails as invalid
      emailCounts.forEach((indices, email) => {
        if (indices.length > 1) {
          indices.forEach(index => {
            transformedData[index].isValid = false;
            transformedData[index].errors.push(`Duplicate email in CSV (appears ${indices.length} times)`);
          });
        }
      });

      // Check which emails already exist in database
      const validEmails = transformedData
        .filter(row => row.isValid && row.data.email)
        .map(row => row.data.email);

      if (validEmails.length > 0) {
        const backendUrl = process.env.NODE_ENV === 'production' 
          ? 'https://links.usehypwave.com/api'
          : 'http://localhost:3001/api';
        
        const response = await fetch(`${backendUrl}/publishers/check-emails`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
          body: JSON.stringify({ emails: validEmails }),
        });
        
        if (response.ok) {
          const result = await response.json();
          const existingEmails = new Set(result.existingEmails || []);
          
          // Mark rows with existing emails as invalid
          transformedData.forEach(row => {
            const email = row.data.email?.toLowerCase();
            if (existingEmails.has(email)) {
              row.isValid = false;
              row.errors.push('Publisher with this email already exists in database');
            }
          });
        }
      }

      setPreviewData(transformedData);
      
      // Auto-select all valid rows
      const validRowIndexes = transformedData
        .filter(row => row.isValid)
        .map(row => row.rowIndex);
      setSelectedRows(validRowIndexes);
      
      setActiveStep(2);
    } catch (error) {
      console.error('Error checking duplicates:', error);
      setError('Failed to validate data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewComplete = (selectedRowIndexes: number[]) => {
    setSelectedRows(selectedRowIndexes);
    setActiveStep(3);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');

      // Prepare data for backend
      const publishersToCreate = selectedRows.map(rowIndex => {
        const row = previewData[rowIndex];
        
        // Convert isActive to boolean (CSV might have string "true"/"false")
        let isActive = true;
        if (row.data.isActive !== undefined && row.data.isActive !== null) {
          if (typeof row.data.isActive === 'boolean') {
            isActive = row.data.isActive;
          } else if (typeof row.data.isActive === 'string') {
            isActive = row.data.isActive.toLowerCase() === 'true';
          }
        }
        
        return {
          publisherName: row.data.publisherName,
          email: row.data.email,
          whatsapp: row.data.whatsapp || '',
          modeOfCommunication: row.data.modeOfCommunication || 'EMAIL',
          isActive: isActive,
        };
      });

      // Call backend API
      const backendUrl = process.env.NODE_ENV === 'production' 
        ? 'https://links.usehypwave.com/api'
        : 'http://localhost:3001/api';
      const response = await fetch(`${backendUrl}/publishers/bulk-upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ publishers: publishersToCreate }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to upload publishers');
      }

      const message = `Successfully created ${result.created} publisher(s)${result.skipped > 0 ? `, skipped ${result.skipped} duplicate(s)` : ''}`;
      onSuccess(message);
      handleClose();
    } catch (error: any) {
      setError('Failed to save data: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
    setError('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <PublisherFileUploadStep
            onFileUpload={handleFileUpload}
            loading={loading}
          />
        );
      case 1:
        return (
          <PublisherColumnMappingStep
            csvColumns={parseData?.headers || []}
            autoMappings={parseData?.autoMappings || []}
            onComplete={handleMappingComplete}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 2:
        return (
          <PublisherPreviewTableStep
            previewData={previewData}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            onComplete={handlePreviewComplete}
            onBack={handleBack}
            loading={loading}
          />
        );
      case 3:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Ready to upload {selectedRows.length} publisher(s)
            </Alert>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button onClick={handleBack} disabled={loading}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading || selectedRows.length === 0}
                startIcon={loading ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                {loading ? 'Uploading...' : `Upload ${selectedRows.length} Publisher(s)`}
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Bulk Upload Publishers</span>
          <Button
            onClick={handleClose}
            sx={{ minWidth: 'auto', p: 0.5 }}
            disabled={loading}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {renderStepContent()}
      </DialogContent>
    </Dialog>
  );
};

export default PublisherBulkUploadModal;
